import { Routes } from '@angular/router';
import { Login } from './components/views/login/login';
import { StudentView } from './components/views/student-view/student-view';
import { CounselorView } from './components/views/counselor-view/counselor-view';
import { DistrictView } from './components/views/district-view/district-view';
import { MyProfile } from './components/views/student-view/profile-page/my-profile';
import { UserProfileView } from './components/common/user-profile-view/user-profile-view';
import { authGuard } from './security/auth.guard';
import { roleGuard } from './security/role.guard';
import { permissionGuard } from './security/permission.guard';
import { USER_PERMISSIONS } from './constants/permissions';
import { Settings } from './components/common/student-nav/settings/settings';
import { HomePage } from './components/views/home-page/home-page';
import { StudentMessagesPage } from './components/views/student-view/student-messages-page/student-messages-page';
import { CounselorMessagesPage } from './components/views/counselor-view/counselor-messages-page/counselor-messages-page';
import { CounselorPosts } from './components/views/counselor-view/counselor-posts/counselor-posts';
import { NetworkPage } from './components/views/student-view/network-page/network-page';
import { Careers } from './components/views/student-view/careers/careers';
import { CounselorCareers } from './components/views/counselor-view/counselor-careers/counselor-careers';
import { CounselorCareerEdit } from './components/views/counselor-view/counselor-careers/counselor-careers-edit/counselor-careers-edit';
import { AdaMenu } from './components/common/ada-menu/ada-menu';
import { AuditLogs } from './components/common/audit-logs/audit-logs';
import { CounselorStudents } from './components/views/counselor-view/counselor-students/counselor-students';
import { CounselorCareerPage } from './components/common/counselor-career-page/counselor-career-page';
import { CounselorMeetings } from './components/views/counselor-view/counselor-meetings/counselor-meetings';
import { CounselorMeetingPreferences } from './components/views/counselor-view/counselor-meetings/counselor-meeting-preferences/counselor-meeting-preferences';
import { CounselorMeetingsBookings } from './components/views/counselor-view/counselor-meetings/counselor-meetings-bookings/counselor-meetings-bookings';
import { CounselorMeetingsPending } from './components/views/counselor-view/counselor-meetings/counselor-meetings-pending/counselor-meetings-pending';
import { PrivacyAgreement } from './components/common/privacy-agreement/privacy-agreement';
import { GetStarted } from './components/common/get-started/get-started';
import { CounselorCalendar } from './components/views/counselor-view/counselor-calendar/counselor-calendar';
import { StudentCalendar } from './components/views/student-view/student-calendar/student-calendar';
import { Meetings } from './components/views/student-view/meetings/meetings';
import { SearchResults } from './components/views/student-view/search-results/search-results';
import { Moderation } from './components/views/counselor-view/moderation/moderation';
import { Insights } from './components/views/counselor-view/insights/insights';
import { ModerationReports } from './components/views/counselor-view/moderation/moderation-reports/moderation-reports';
import { WordFilter } from './components/views/counselor-view/moderation/word-filter/word-filter';
import { UserManagement } from './components/views/counselor-view/user-management/user-management';
import { ManageUser } from './components/views/counselor-view/user-management/manage-user/manage-user';
import { ManageRoles } from './components/views/counselor-view/user-management/manage-roles/manage-roles';
import { SISIntegration } from './components/views/district-view/sis-integration/sis-integration';
import { DistrictReports } from './components/views/district-view/district-reports/district-reports';
import { CounselorReports } from './components/views/counselor-view/counselor-reports/counselor-reports';
import { AssignStudents } from './components/views/counselor-view/counselor-students/assign-students/assign-students';
import { GroupPage } from './components/views/counselor-view/counselor-students/group-page/group-page';

export const routes: Routes = [
  { path: 'home', component: HomePage },
  { path: 'login', component: Login },

  {
    path: 'students',
    component: StudentView,
    canActivate: [authGuard],
  },
  {
    path: 'counselors',
    component: CounselorView,
    canActivate: [authGuard, roleGuard('faculty')],
  },
  {
    path: 'districts',
    component: DistrictView,
    canActivate: [authGuard, roleGuard('district')],
  },
  {
    path: 'counselors/posts',
    component: CounselorPosts,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.POSTS.VIEW)],
  },
  {
    path: 'counselors/reports',
    component: CounselorReports,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.REPORTS.VIEW)],
  },
  {
    path: 'profilePage',
    component: MyProfile,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.PROFILE.EDIT_OWN)],
  },
  {
    path: 'profile/:userId',
    component: UserProfileView,
    canActivate: [authGuard],
  },
  {
    path: 'privacy',
    component: PrivacyAgreement,
  },
  {
    path: 'settings',
    component: Settings,
    canActivate: [authGuard],
  },
  {
    path: 'messages',
    component: StudentMessagesPage,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.MESSAGING.VIEW)],
  },
  {
    path: 'meetings',
    component: Meetings,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.MEETINGS.VIEW)],
  },
  {
    path: 'search',
    component: SearchResults,
    canActivate: [authGuard],
  },
  {
    path: 'counselors/careers/edit',
    component: CounselorCareerEdit,
    canActivate: [
      authGuard,
      permissionGuard([USER_PERMISSIONS.CAREER.CREATE, USER_PERMISSIONS.CAREER.EDIT]),
    ],
  },
  {
    path: 'counselors/careers/edit/:id',
    component: CounselorCareerEdit,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.CAREER.EDIT)],
  },
  {
    path: 'careers',
    component: Careers,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.CAREER.VIEW)],
  },
  {
    path: 'audit-logs',
    component: AuditLogs,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.AUDIT.VIEW_LOGS)],
  },
  {
    path: 'counselors/messages',
    component: CounselorMessagesPage,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.MESSAGING.VIEW)],
  },
  {
    path: 'counselors/calendar',
    component: CounselorCalendar,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.CALENDAR.VIEW)],
  },
  {
    path: 'students/calendar',
    component: StudentCalendar,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.CALENDAR.VIEW)],
  },
  {
    path: 'network',
    component: NetworkPage,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.STUDENTS.VIEW_NETWORK)],
  },
  {
    path: 'counselors/careers',
    component: CounselorCareers,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.CAREER.VIEW)],
  },
  {
    path: 'counselors/students',
    component: CounselorStudents,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.STUDENTS.VIEW_ASSIGNED)],
  },
  {
    path: 'counselors/students/assign',
    component: AssignStudents,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.STUDENTS.VIEW_ASSIGNED)],
  },
  {
    path: 'counselors/students/groups/:groupId',
    component: GroupPage,
  },
  {
    path: 'counselors/meetings',
    component: CounselorMeetings,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.MEETINGS.VIEW)],
    children: [
      {
        path: '',
        redirectTo: 'bookings',
        pathMatch: 'full',
      },
      {
        path: 'preferences',
        component: CounselorMeetingPreferences,
      },
      {
        path: 'bookings',
        component: CounselorMeetingsBookings,
      },
      {
        path: 'pending',
        component: CounselorMeetingsPending,
      },
    ],
  },
  {
    path: 'counselors/moderation',
    component: Moderation,
    canActivate: [
      authGuard,
      permissionGuard([
        USER_PERMISSIONS.MODERATION.VIEW_REPORTED,
        USER_PERMISSIONS.MODERATION.MANAGE_TERMS,
      ]),
    ],
    children: [
      {
        path: '',
        redirectTo: 'reports',
        pathMatch: 'full',
      },
      {
        path: 'reports',
        component: ModerationReports,
        canActivate: [permissionGuard(USER_PERMISSIONS.MODERATION.VIEW_REPORTED)],
      },
      {
        path: 'terms',
        component: WordFilter,
        canActivate: [permissionGuard(USER_PERMISSIONS.MODERATION.MANAGE_TERMS)],
      },
    ],
  },
  {
    path: 'counselors/users',
    component: UserManagement,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.USERS.VIEW)],
  },
  {
    path: 'counselors/roles',
    component: ManageRoles,
    canActivate: [
      authGuard,
      permissionGuard([
        USER_PERMISSIONS.ROLES.VIEW,
        USER_PERMISSIONS.ROLES.CREATE,
        USER_PERMISSIONS.ROLES.EDIT,
        USER_PERMISSIONS.ROLES.DELETE,
        USER_PERMISSIONS.ROLES.MANAGE_PERMISSIONS,
      ]),
    ],
  },
  {
    path: 'counselors/users/:id',
    component: ManageUser,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.USERS.ASSIGN_ROLES)],
  },
  {
    path: 'counselors/careers/:id',
    component: CounselorCareerPage,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.CAREER.VIEW)],
  },
  {
    path: 'counselors/insights',
    component: Insights,
    canActivate: [authGuard, permissionGuard(USER_PERMISSIONS.ANALYTICS.VIEW)],
  },
  {
    path: 'districts/integration',
    component: SISIntegration,
    canActivate: [authGuard, roleGuard('district')],
  },
  {
    path: 'districts/reports',
    component: DistrictReports,
    canActivate: [authGuard, roleGuard('district')],
  },
  { path: 'settings', component: Settings, canActivate: [authGuard] },
  {
    path: 'ada',
    component: AdaMenu,
  },
  {
    path: 'getStarted',
    component: GetStarted,
  },

  /// always bottom
  { path: '**', redirectTo: '/home' },
];
